import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Trophy, X, Gift, ChevronRight, Star } from 'lucide-react';
import { useAuctionSounds } from '../../hooks/useAuctionSounds';
import { auctionPath } from '../../utils/publicIds';
import { getWinningBidPresentation, isOneOnOneDelivery } from '../../utils/auctionLabels';

interface AuctionWinnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  auctionId?: number;
  auctionTitle?: string;
  winningBid?: number | string;
  auctionType?: string;
  gameType?: string;
  purchaseType?: 'buy_now' | 'bid_winner';
}

// ─── Confetti particle ────────────────────────────────────────────────────────
const COLORS = ['#f59e0b', '#10b981', '#6366f1', '#ec4899', '#f97316', '#06b6d4', '#84cc16', '#fff'];

const Particle: React.FC<{ index: number }> = ({ index }) => {
  const left = `${(index / 28) * 100}%`;
  const duration = 1.4 + (index % 5) * 0.25;
  const color = COLORS[index % COLORS.length];
  const size = index % 3 === 0 ? 'w-2.5 h-2.5' : 'w-1.5 h-1.5';
  return (
    <motion.div
      className={`absolute ${size} rounded-sm pointer-events-none`}
      style={{ backgroundColor: color, left, top: '-10px' }}
      initial={{ y: 0, opacity: 1, scale: 1, rotate: 0 }}
      animate={{ y: 500, opacity: 0, scale: 0.2, rotate: (index % 2 === 0 ? 1 : -1) * (180 + index * 12) }}
      transition={{ duration, delay: index * 0.04, ease: 'easeIn' }}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const AuctionWinnerModal: React.FC<AuctionWinnerModalProps> = ({
  isOpen, onClose, auctionId, auctionTitle, winningBid, auctionType, gameType, purchaseType,
}) => {
  const navigate = useNavigate();
  const { play } = useAuctionSounds(true);
  const soundPlayedRef = useRef(false);
  const winningBidPresentation = getWinningBidPresentation(gameType, purchaseType);

  useEffect(() => {
    if (!isOpen) {
      soundPlayedRef.current = false;
      return;
    }
    if (soundPlayedRef.current) return;
    soundPlayedRef.current = true;
    const t = setTimeout(() => play('winner_fanfare'), 250);
    return () => clearTimeout(t);
  }, [isOpen, play]);

  const handleClaim = () => {
    onClose();
    navigate(auctionId ? auctionPath(auctionId) : '/game-a-rama/my-wins');
  };

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* ── Backdrop ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/75 backdrop-blur-md z-[9998]"
            aria-hidden="true"
            onClick={onClose}
          />

          {/* ── Modal sheet ── */}
          <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 mobile-modal-viewport">
            <motion.div
              initial={{ opacity: 0, y: '100%', scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: '100%', scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              className="
                relative w-full overflow-hidden
                rounded-t-3xl sm:rounded-3xl
                max-h-[92dvh] sm:max-h-[90vh] mobile-modal-sheet
                sm:w-[min(420px,calc(100vw-2rem))]
                shadow-2xl
              "
              role="dialog"
              aria-modal="true"
              aria-label="Game-a-Rama Winner"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Confetti layer */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
                {Array.from({ length: 28 }, (_, i) => <Particle key={i} index={i} />)}
              </div>

              {/* Background — dark always (celebration look) */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#2d1a05] via-[#1a0f2e] to-[#0d1333]" />

              {/* Gold accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-600 via-yellow-300 to-amber-600" />

              {/* Drag handle (mobile) */}
              <div className="relative z-20 flex justify-center pt-3 sm:hidden">
                <div className="w-10 h-1 rounded-full bg-white/30" />
              </div>

              {/* Content */}
              <div className="relative z-20 px-5 pt-2 sm:px-6 sm:pt-4 overflow-y-auto max-h-[90dvh] sm:max-h-none mobile-modal-scroll mobile-modal-footer">
                {/* Close */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-4 sm:top-4 sm:right-5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center transition-colors touch-manipulation"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-white" />
                </button>

                {/* Trophy */}
                <motion.div
                  animate={{ rotate: [-4, 4, -4], scale: [1, 1.06, 1] }}
                  transition={{ duration: 2.2, repeat: Infinity }}
                  className="mx-auto mb-3 sm:mb-4 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-[0_0_40px_rgba(245,158,11,0.55)]"
                >
                  <Trophy className="w-10 h-10 sm:w-12 sm:h-12 text-white drop-shadow-lg" />
                </motion.div>

                {/* Stars */}
                <div className="flex justify-center gap-1 mb-2 sm:mb-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <motion.div key={i} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.08, type: 'spring' }}>
                      <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" />
                    </motion.div>
                  ))}
                </div>

                {/* Headline */}
                <motion.h2
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 }}
                  className="text-center text-xl sm:text-2xl font-black text-white tracking-tight mb-1"
                >
                  {purchaseType === 'buy_now' ? 'PURCHASE COMPLETE!' : 'YOU ARE THE WINNER!'}
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.28 }}
                  className="text-center text-amber-400 font-semibold text-xs sm:text-sm mb-4"
                >
                  {purchaseType === 'buy_now'
                    ? 'You have successfully purchased this item'
                    : 'Congratulations — your entry won the Game-a-Rama event'}
                </motion.p>

                {/* Info card */}
                {(auctionTitle || winningBid) && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.38 }}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-3.5 sm:p-4 mb-4"
                  >
                    {auctionTitle && (
                      <div className="mb-2.5">
                        <p className="text-white/50 text-[10px] sm:text-xs uppercase tracking-wider mb-0.5">Game-a-Rama</p>
                        <p className="text-white font-bold text-sm sm:text-base line-clamp-2 leading-snug">{auctionTitle}</p>
                      </div>
                    )}
                    {winningBid && (
                      <div>
                        <p className="text-white/50 text-[10px] sm:text-xs uppercase tracking-wider mb-0.5">
                          {winningBidPresentation.label}
                        </p>
                        <p className="text-amber-400 font-black text-2xl sm:text-3xl leading-none">
                          {typeof winningBid === 'number' ? `${winningBid.toLocaleString()}${winningBidPresentation.unit}` : winningBid}
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Prize type */}
                {auctionType && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.46 }}
                    className="flex items-center justify-center gap-1.5 mb-4"
                  >
                    <Gift className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    <span className="text-emerald-400 text-xs font-semibold">
                      {auctionType === 'physical' ? 'Physical Product — Delivery Required'
                       : auctionType === 'digital' ? 'Digital Product — Instant Claim'
                       : isOneOnOneDelivery(auctionType) ? 'Creator Services'
                       : 'Prize Ready to Claim'}
                    </span>
                  </motion.div>
                )}

                {/* CTAs */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col gap-3"
                >
                  <button
                    onClick={handleClaim}
                    className="
                      w-full py-3.5 sm:py-4 rounded-2xl
                      bg-gradient-to-r from-amber-500 to-amber-600
                      hover:from-amber-400 hover:to-amber-500
                      active:scale-[0.98]
                      text-white font-black text-base sm:text-lg
                      transition-all shadow-lg shadow-amber-500/30
                      flex items-center justify-center gap-2
                      touch-manipulation
                    "
                  >
                    Claim Your Prize
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={onClose}
                    className="
                      w-full py-3 sm:py-3.5 rounded-2xl
                      bg-white/10 hover:bg-white/15 active:bg-white/20
                      text-white/80 font-semibold text-sm sm:text-base
                      transition-all touch-manipulation
                    "
                  >
                    View Later
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
};

export default AuctionWinnerModal;
